import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { AppVersionApi } from "../../../../../../../../services/Api/Admin/adminTypes";

interface ListState {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const listName = "APPS_VERSION";
const resourceType = "APPS_VERSION";

const List = ({ resource, mobileActions }: ListState) => {
	const { t } = useTranslation("admin");
	const confirmation = useConfirmation();
	const { addSuccessFlash } = useFlash();
	const location = useLocation();
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const onRemoveAppVersion = async (item: AppVersionApi) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		await adminApi.removeAppVersion(item.id);
		addSuccessFlash(t("lib:common.flash.removed"));
	};

	const replaceLink = (link: string) => {
		if (link.includes("http://")) {
			link.replace("http://", "https://");
		}
		return link;
	};

	let config = {
		fields: [
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
				render: (item: AppVersionApi) => <Link to={`/admin/app_versions/${item.id}`}>{item.name}</Link>,
				renderExport: (item: AppVersionApi) => item.name,
			},
			{
				id: "app_type",
				name: t("lib:common.word.type"),
				type: "list",
				listOptions: {
					POS: t("enums.app_versions.type.POS"),
					SERVER: t("enums.app_versions.type.SERVER"),
					GASTRO_CONTROL: t("enums.app_versions.type.GASTRO_CONTROL"),
					UPDATE: t("enums.app_versions.type.UPDATE"),
					SELF_UPDATE: t("enums.app_versions.type.SELF_UPDATE"),
					WEIGHT_SCALE: t("enums.app_versions.type.WEIGHT_SCALE"),
					MIGRATIONS: t("enums.app_versions.type.MIGRATIONS"),
					CUSTOMER_DISPLAY: t("enums.app_versions.type.CUSTOMER_DISPLAY"),
					HOTEL_INTEGRATION: t("enums.app_versions.type.HOTEL_INTEGRATION"),
					DELIVERY: t("enums.app_versions.type.DELIVERY"),
					ANDROID_POS: t("enums.app_versions.type.ANDROID_POS"),
					ANDROID_POS_V2: t("enums.app_versions.type.ANDROID_POS_V2"),
					ANDROID_POS_V2_MAPPING: t("enums.app_versions.type.ANDROID_POS_V2_MAPPING"),
					ANDROID_KDS: t("enums.app_versions.type.ANDROID_KDS"),
					ANDROID_INVENTORY: t("enums.app_versions.type.ANDROID_INVENTORY"),
					ANDROID_MANAGER: t("enums.app_versions.type.ANDROID_MANAGER"),
					IOS_MANAGER: t("enums.app_versions.type.IOS_MANAGER"),
					ANDROID_KIOSK: t("enums.app_versions.type.ANDROID_KIOSK"),
					ANDROID_LAUNCHER: t("enums.app_versions.type.ANDROID_LAUNCHER"),
					ANDROID_TEAM_VIEWER: t("enums.app_versions.type.ANDROID_TEAM_VIEWER"),
					ANDROID_PHONE_CENTRAL: t("enums.app_versions.type.ANDROID_PHONE_CENTRAL"),
					ANDROID_TEAM_VIEWER_HOST: t("enums.app_versions.type.ANDROID_TEAM_VIEWER_HOST"),
					ANDROID_AIR_DROID: t("enums.app_versions.type.ANDROID_AIR_DROID"),
					ANDROID_CUSTOMER_DISPLAY: t("enums.app_versions.type.ANDROID_CUSTOMER_DISPLAY"),
					ANDROID_ORDER: t("enums.app_versions.type.ANDROID_ORDER"),
					ANDROID_ANYDESK: t("enums.app_versions.type.ANDROID_ANYDESK"),
					ANDROID_TEAM_VIEWER_QUICK_SUPPORT: t("enums.app_versions.type.ANDROID_TEAM_VIEWER_QUICK_SUPPORT"),
					ANDROID_ANY_DESK_ADDON: t("enums.app_versions.type.ANDROID_ANY_DESK_ADDON"),
					ANDROID_TEAM_VIEWER_UNIVERSAL_ADDON: t(
						"enums.app_versions.type.ANDROID_TEAM_VIEWER_UNIVERSAL_ADDON"
					),
					ANDROID_ESERVICE_GPTOM: t("enums.app_versions.type.ANDROID_ESERVICE_GPTOM"),
					ANDROID_XPAY_XKASA: t("enums.app_versions.type.ANDROID_XPAY_XKASA"),
				},
				render: (item: AppVersionApi) => (
					<Link to={`/admin/app_versions/${item.id}`}>{t(`enums.app_versions.type.${item.app_type}`)}</Link>
				),
				renderExport: (item: AppVersionApi) => item.app_type,
			},
			{
				id: "version",
				name: t("modules.app_version.field.version.title"),
				type: "text",
				render: (item: AppVersionApi) => item.version,
				renderExport: (item: AppVersionApi) => item.version,
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (item: AppVersionApi) => FormatDate(item.created_at),
				renderExport: (item: AppVersionApi) => FormatDate(item.created_at),
			},
			{
				id: "description",
				name: t("lib:common.word.description"),
				render: (item: AppVersionApi) => item.description,
				renderExport: (item: AppVersionApi) => item.description,
			},
			{
				id: "change_log",
				name: t("modules.app_version.field.changes.title"),
				render: (item: AppVersionApi) => item.change_log,
				renderExport: (item: AppVersionApi) => item.change_log,
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				listOptions: {
					DRAFT: t("enums.app_versions.status.DRAFT"),
					PROD: t("enums.app_versions.status.PROD"),
					TEST: t("enums.app_versions.status.TEST"),
				},
				render: (item: AppVersionApi) => t(`enums.app_versions.status.${item.status}`),
				renderExport: (item: AppVersionApi) => t(`enums.app_versions.status.${item.status}`),
			},
			{
				id: "link",
				name: t("modules.app_version.field.link.title"),
				render: (item: AppVersionApi) => (
					<a href={replaceLink(item.link)} download target="_blank" rel="noreferrer">
						{item.link}
					</a>
				),
			},
		],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: AppVersionApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: AppVersionApi) => onRemoveAppVersion(item),
				refreshListAfter: true,
			},
		],
		selectedColumns: ["name", "app_type", "version", "created_at", "description", "change_log", "status", "link"],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			return adminApi.getAppVersions(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		numberOfStickyColumnsAtTheStart: 2,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};
	return <ListData config={config} mobileActions={mobileActions} />;
};

const AdminAppVersionsIndexPage = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: "/admin/app_versions/new",
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: "/admin/app_versions/new",
		},
	];
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.app_version.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.app_version.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} mobileActions={mobileActions} />
			</Suspense>
		</div>
	);
};

export default AdminAppVersionsIndexPage;
